import * as React from 'react';
import { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Switch, Container, Tooltip, Fade, useMediaQuery, useTheme } from '@mui/material';
import { useThemeContext } from '../contexts/ThemeContext.tsx';
import { jwtDecode } from 'jwt-decode';
import Cookies from 'js-cookie';
import { styled } from "@mui/system";
import LLLogo from "../assets/images/LL2.png"; 

// Ikonok a váltóhoz
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';

// --- Animációk (Keyframes) ---
const keyframes = `
@keyframes backgroundMove {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
@keyframes shineActiveDark {
  0% { box-shadow: 0 0 5px #ff4081; }
  50% { box-shadow: 0 0 15px #ff80ab, 0 0 20px #ff4081; }
  100% { box-shadow: 0 0 5px #ff4081; }
}
@keyframes shineActiveLight {
  0% { box-shadow: 0 0 5px #FFD700; }
  50% { box-shadow: 0 0 15px #FFFACD, 0 0 20px #FFD700; }
  100% { box-shadow: 0 0 5px #FFD700; }
}
`;

// --- Styled ThemeToggle ---
const ThemeToggle = styled(Switch)(({ theme }) => ({
    width: 62,
    height: 36,
    padding: 7,
    "& .MuiSwitch-switchBase": {
        margin: 9,
        padding: 0,
        transform: "translateX(0px)",
        transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
        "&.Mui-checked": {
            transform: "translateX(28px)",
            color: "#fff",
            "& .MuiSwitch-thumb": {
                backgroundColor: "#2c3e50",
                boxShadow: "0 0 10px rgba(0,0,0,0.5)",
            },
            "& + .MuiSwitch-track": {
                opacity: 1,
                background: "linear-gradient(to right, #2c3e50, #3498db)",
            },
        },
        "& .MuiSwitch-thumb": {
            backgroundColor: "#FFD700",
            boxShadow: "0 0 10px rgba(255, 215, 0, 0.7)",
        },
    },
    "& .MuiSwitch-track": {
        borderRadius: 11,
        opacity: 1,
        background: "linear-gradient(to right, #ffe603fd, #8a8701ff)",
        boxSizing: "border-box",
        transition: "background 0.35s ease",
    },
}));

// --- Animált AppBar ---
const AnimatedAppBar = styled(AppBar, {
    shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
    background: darkMode 
        ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
        : 'linear-gradient(45deg, #25379c 30%, #21cbf3 90%)',
    backgroundSize: '200% 200%',
    animation: 'backgroundMove 12s ease infinite',
    zIndex: 1100,
    boxShadow: darkMode ? '0px 4px 15px rgba(255, 0, 0, 0.15)' : '0px 4px 15px rgba(33, 203, 243, 0.2)',
}));

export default function Navbar() {
  const [auth, setAuth] = useState(false);
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [anchorElApps, setAnchorElApps] = useState<null | HTMLElement>(null);
  const [anchorElWebsites, setAnchorElWebsites] = useState<null | HTMLElement>(null);
  const [anchorElGames, setAnchorElGames] = useState<null | HTMLElement>(null); 
  const [anchorElLeaderBoards, setAnchorElLeaderBoards] = useState<null | HTMLElement>(null); 

  const { darkMode, toggleDarkMode } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const token = Cookies.get('token');
    if (token) {
      try {
        const decoded = jwtDecode<{exp: number}>(token);
        if (decoded.exp * 1000 < Date.now()) {
          Cookies.remove('token');
          navigate('/login');
        } else { setAuth(true); }
      } catch (error) { navigate('/login'); }
    }
  }, [navigate]);

  const isGamesActive = () => ['/click', '/aimtester', '/caseopening', '/catchme', '/leaderboard', '/aimleaderboard'].some(path => location.pathname === path);
  const isAppsActive = () => ['/encryption', '/randomizer', '/pictureconverter', '/valentincard', '/freewebsites', '/freetrialwebsites', '/paidwebsites'].some(path => location.pathname === path);
  const isActive = (path: string) => location.pathname === path;

  const getButtonStyle = (path: string, specialCondition?: boolean) => {
    const active = specialCondition || isActive(path);
    const activeColor = darkMode ? '#ff4081' : '#FFD700';
    const activeBg = darkMode ? 'rgba(255, 64, 129, 0.25)' : 'rgba(255, 215, 0, 0.25)';
    const animationName = darkMode ? 'shineActiveDark' : 'shineActiveLight';

    return {
        my: 1.8, color: 'white', mx: 0.5, px: 2,
        borderRadius: '20px',
        textTransform: 'none',
        fontSize: '0.9rem',
        fontWeight: active ? 'bold' : 'normal',
        transition: 'all 0.3s ease',
        animation: active ? `${animationName} 2s infinite alternate` : 'none',
        backgroundColor: active ? activeBg : 'transparent',
        borderBottom: active ? `2px solid ${activeColor}` : '2px solid transparent',
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.15)', transform: 'translateY(-1px)' }
    };
  };

  const dynamicMenuStyle = {
    elevation: 8,
    sx: {
        overflow: 'visible',
        mt: 1.5,
        borderRadius: '12px',
        border: darkMode ? '1px solid rgba(255, 0, 0, 0.2)' : '1px solid rgba(255, 215, 0, 0.3)',
        background: darkMode 
            ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
            : 'linear-gradient(45deg, #25379c 30%, #21cbf3 90%)',
        backgroundSize: '200% 200%',
        animation: 'backgroundMove 12s ease infinite',
        '& .MuiMenuItem-root': {
            borderRadius: '8px',
            margin: '4px 8px',
            color: 'white',
            transition: 'all 0.2s ease',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
        },
    },
  };

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorElNav(event.currentTarget);
  const handleCloseNavMenu = () => setAnchorElNav(null);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <style>{keyframes}</style>
      <AnimatedAppBar position="fixed" darkMode={darkMode}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* MOBIL: Hamburger Menü */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }}>
              <IconButton size="large" onClick={handleOpenNavMenu} color="inherit">
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorElNav}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                disableScrollLock
                TransitionComponent={Fade}
                slotProps={{ paper: dynamicMenuStyle }}
                sx={{ display: { xs: 'block', md: 'none' } }}
              >
                <MenuItem onClick={() => { navigate('/linklearning'); handleCloseNavMenu(); }}>Explore</MenuItem>
                <MenuItem onClick={() => { navigate('/learning'); handleCloseNavMenu(); }}>Learning</MenuItem>
                <MenuItem onClick={(e) => setAnchorElGames(e.currentTarget)}>Games »</MenuItem>
                <MenuItem onClick={(e) => setAnchorElApps(e.currentTarget)}>Websites and Apps »</MenuItem>
              </Menu>
            </Box>

            {/* Dark Mode Switch (Mindig látszik) */}
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                <ThemeToggle
                    checked={darkMode}
                    onChange={() => { toggleDarkMode(); sessionStorage.setItem('themeMode', darkMode ? 'light' : 'dark'); }}
                    icon={<LightModeIcon sx={{ fontSize: 18, color: "white" }} />}
                    checkedIcon={<DarkModeIcon sx={{ fontSize: 16, color: "#fff" }} />}
                />
            </Box>
            
            {/* Logo (Desktopon balra, Mobilon középen/balra) */}
            <Typography variant="h6" noWrap component="a" onClick={() => navigate('/')}
              sx={{ flexGrow: { xs: 1, md: 0 }, mr: 2, display: 'flex', cursor: 'pointer' }}>
              <Box component="img" src={LLLogo} alt="Logo" sx={{ height: { xs: 40, md: 50 }, width: "auto" }} />
            </Typography>
            
            {/* ASZTALI NAVIGÁCIÓ */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
              <Button sx={getButtonStyle('/linklearning')} onClick={() => navigate('/linklearning')}>Explore</Button>
              <Button sx={getButtonStyle('/learning')} onClick={() => navigate('/learning')}>Learning</Button>
              <Button sx={getButtonStyle('/games', isGamesActive())} onClick={(e) => setAnchorElGames(e.currentTarget)}>Games</Button>
              <Button sx={getButtonStyle('/apps', isAppsActive())} onClick={(e) => setAnchorElApps(e.currentTarget)}>Websites and Apps</Button>
            </Box>

            {/* MENÜK (Közös Games/Apps logika desktopra és mobilra) */}
            <Menu 
              anchorEl={anchorElGames} open={Boolean(anchorElGames)} onClose={() => setAnchorElGames(null)}
              disableScrollLock TransitionComponent={Fade} slotProps={{ paper: dynamicMenuStyle }}
              anchorOrigin={{ vertical: isMobile ? 'center' : 'bottom', horizontal: isMobile ? 'right' : 'left' }}
            >
              <MenuItem onClick={() => { navigate('/click'); setAnchorElGames(null); handleCloseNavMenu(); }}>Clicker</MenuItem>
              <MenuItem onClick={() => { navigate('/aimtester'); setAnchorElGames(null); handleCloseNavMenu(); }}>Aim Tester</MenuItem>
              <MenuItem onClick={() => { navigate('/caseopening'); setAnchorElGames(null); handleCloseNavMenu(); }}>Case Opening</MenuItem>
              <MenuItem onClick={() => { navigate('/catchme'); setAnchorElGames(null); handleCloseNavMenu(); }}>Catch me</MenuItem>
              <MenuItem onClick={(e) => setAnchorElLeaderBoards(e.currentTarget)}><strong>Leaderboards »</strong></MenuItem>
            </Menu>

            <Menu 
              anchorEl={anchorElLeaderBoards} open={Boolean(anchorElLeaderBoards)} onClose={() => {setAnchorElLeaderBoards(null); setAnchorElGames(null);}}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }} 
              disableScrollLock TransitionComponent={Fade} slotProps={{ paper: dynamicMenuStyle }}
            >
              <MenuItem onClick={() => { navigate('/leaderboard'); setAnchorElLeaderBoards(null); setAnchorElGames(null); handleCloseNavMenu(); }}>Clicker Leaderboard</MenuItem>
              <MenuItem onClick={() => { navigate('/aimleaderboard'); setAnchorElLeaderBoards(null); setAnchorElGames(null); handleCloseNavMenu(); }}>AIM Leaderboard</MenuItem>
            </Menu>

            <Menu 
              anchorEl={anchorElApps} open={Boolean(anchorElApps)} onClose={() => setAnchorElApps(null)}
              disableScrollLock TransitionComponent={Fade} slotProps={{ paper: dynamicMenuStyle }}
              anchorOrigin={{ vertical: isMobile ? 'center' : 'bottom', horizontal: isMobile ? 'right' : 'left' }}
            >
              <MenuItem onClick={() => { navigate('/encryption'); setAnchorElApps(null); handleCloseNavMenu(); }}>Encryption</MenuItem>
              <MenuItem onClick={() => { navigate('/randomizer'); setAnchorElApps(null); handleCloseNavMenu(); }}>Randomizer</MenuItem>
              <MenuItem onClick={() => { navigate('/pictureconverter'); setAnchorElApps(null); handleCloseNavMenu(); }}>Picture Converters</MenuItem>
              <MenuItem onClick={() => { navigate('/valentincard'); setAnchorElApps(null); handleCloseNavMenu(); }}>Valentin Card</MenuItem>
              <MenuItem onClick={(e) => setAnchorElWebsites(e.currentTarget)}><strong>Powerful Websites »</strong></MenuItem>
            </Menu>

            <Menu 
              anchorEl={anchorElWebsites} open={Boolean(anchorElWebsites)} onClose={() => {setAnchorElWebsites(null); setAnchorElApps(null);}}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }} 
              disableScrollLock TransitionComponent={Fade} slotProps={{ paper: dynamicMenuStyle }}
            >
              <MenuItem onClick={() => { navigate('/freewebsites'); setAnchorElWebsites(null); setAnchorElApps(null); handleCloseNavMenu(); }}>Free Websites</MenuItem>
              <MenuItem onClick={() => { navigate('/freetrialwebsites'); setAnchorElWebsites(null); setAnchorElApps(null); handleCloseNavMenu(); }}>Free Trial Websites</MenuItem>
              <MenuItem onClick={() => { navigate('/paidwebsites'); setAnchorElWebsites(null); setAnchorElApps(null); handleCloseNavMenu(); }}>Paid Websites</MenuItem>
            </Menu>

            {/* Auth / Profile (Mindig látszik a jobb szélen) */}
            <Box sx={{ flexGrow: 0, ml: 1 }}>
              {!auth ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {!isMobile && (
                    <>
                      <Button sx={getButtonStyle('/login')} component={Link} to="/login">Sign In</Button>
                      <Button sx={getButtonStyle('/register')} component={Link} to="/register">Sign Up</Button>
                    </>
                  )}
                  {isMobile && (
                    <IconButton color="inherit" onClick={() => navigate('/login')}>
                      <AccountCircle />
                    </IconButton>
                  )}
                </Box>
              ) : (
                <>
                  <IconButton onClick={(e) => setAnchorElUser(e.currentTarget)} color="inherit"><AccountCircle /></IconButton>
                  <Menu 
                    anchorEl={anchorElUser} open={Boolean(anchorElUser)} onClose={() => setAnchorElUser(null)}
                    disableScrollLock TransitionComponent={Fade} slotProps={{ paper: dynamicMenuStyle }}
                  >
                    <MenuItem onClick={() => { navigate('/profile'); setAnchorElUser(null); }}>Profile</MenuItem>
                    <MenuItem onClick={() => { 
                        Cookies.remove('token'); 
                        setAuth(false); 
                        setAnchorElUser(null);
                        navigate('/login'); 
                    }}>Logout</MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AnimatedAppBar>
    </Box>
  );
}